import { NgTemplateOutlet } from "@angular/common";
import {
  afterRenderEffect,
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  LOCALE_ID,
} from "@angular/core";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { Meta } from "@angular/platform-browser";
import {
  NavigationCancel,
  NavigationEnd,
  NavigationError,
  NavigationStart,
  Router,
  RouterOutlet,
} from "@angular/router";
import { PrimeNGConfig } from "primeng/api";
import type { APTContext } from "src/app/services/aptContext/apt-context";
import {
  DEFAULT_MENU_ZINDEX,
  DEFAULT_MODAL_ZINDEX,
  DEFAULT_OVERLAY_ZINDEX,
  DEFAULT_TOOLTIP_ZINDEX,
  DEFAULT_WINDOW_ZINDEX,
  siteDescription,
} from "src/config";
import { LoggedInLayoutComponent } from "src/standard/layouts/logged-in-layout/logged-in-layout.component";
import type { TelemetryService } from "src/standard/logging/telemetry.service";
import { HttpCancelService } from "src/standard/services/http-cancel.service";
import { lazyInject } from "src/standard/services/lazy-inject.service";
import { StandalonePageService } from "src/standard/services/standalone-page.service";
// import { activePalette$, setPrimaryColor } from "src/standard/ts/theming";
import { userSummary$ } from "../security/security.service";
import NProgress from "../ts/nprogress-customizations";
import { StandardFocusRingHelperComponent } from "src/standard/standard-focus-ring-helper/standard-focus-ring-helper.component";
import { isWorkspaceListRoute } from "src/standard/ts/routeUtils";
import { ENGAGEMENT_MEMBER_FIRM_ID_KEY } from "src/app/classic/classic-interceptor";

export interface RouterData {
  title?: string;
  helpSiteId?: string;
}

@Component({
  standalone: true,
  selector: "app-root",
  templateUrl: "./app.component.html",
  styles: `
    :ng-deep .scrollbar-measure {
      width: 100px;
      height: 100px;
      position: absolute;
      top: -9999px;
    }
  `,
  imports: [
    NgTemplateOutlet,
    RouterOutlet,

    LoggedInLayoutComponent,
    StandardFocusRingHelperComponent,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AppComponent {
  private metaService = inject(Meta);
  ngLocale = inject(LOCALE_ID);

  private readonly router = inject(Router);
  private readonly httpCancelService = inject(HttpCancelService);
  private readonly primengConfig = inject(PrimeNGConfig);
  protected readonly standalonePageService = inject(StandalonePageService);

  // Lazy-inject these so they are not loaded until the app is initialized
  // but they're still available for use in the app
  // and are providedIn root so they stay alive for the lifetime of the app
  private readonly aptContext = lazyInject<APTContext>(
    import("src/app/services/aptContext/apt-context").then((m) => m.APTContext),
  );
  private readonly telemetryService = lazyInject<TelemetryService>(
    import("src/standard/logging/telemetry.service").then(
      (m) => m.TelemetryService,
    ),
  );

  readonly scrollbarWidth = computed(() => {
    // Create the div
    const scrollDiv = document.createElement("div");
    scrollDiv.className = "scrollbar-measure overflow-scroll";
    document.body.appendChild(scrollDiv);

    const withScrollbar = scrollDiv.clientWidth;
    scrollDiv.classList.remove("overflow-scroll");
    scrollDiv.classList.add("overflow-hidden");

    const withoutScrollbar = scrollDiv.clientWidth;

    // Get the scrollbar width
    const scrollbarWidth = withoutScrollbar - withScrollbar;

    // Delete the div
    document.body.removeChild(scrollDiv);

    if (scrollbarWidth) {
      return `${scrollbarWidth}px`;
    }

    return 0;
  });

  private syncCssVariables = afterRenderEffect(() => {
    document.body.style.setProperty(
      "--scrollbar-width",
      this.scrollbarWidth() + "",
    );
  });

  constructor() {
    const ngLocale = this.ngLocale;

    globalThis.__locale = ngLocale;
    globalThis.__rtl = document.dir === "rtl";

    this.primengConfig.zIndex = {
      window: DEFAULT_WINDOW_ZINDEX,
      modal: DEFAULT_MODAL_ZINDEX,
      menu: DEFAULT_MENU_ZINDEX,
      overlay: DEFAULT_OVERLAY_ZINDEX,
      tooltip: DEFAULT_TOOLTIP_ZINDEX,
    };

    // Update the meta site description tag
    this.metaService.updateTag({
      name: "description",
      content: siteDescription,
    });

    this.router.events.pipe(takeUntilDestroyed()).subscribe((event): void => {
      if (event instanceof NavigationStart) {
        this.httpCancelService.cancelPendingRequests();

        NProgress.start();

        if (event.url === "/maintenance" || event.url === "/access-denied") {
          userSummary$.set([true, null]);
        }
      } else if (event instanceof NavigationEnd) {
        NProgress.done();

        const isOnWorkspaceList = isWorkspaceListRoute(event.url);
        if (isOnWorkspaceList) {
          this.clearEngagementStorage();
        }
      } else if (event instanceof NavigationCancel) {
        NProgress.done();
      } else if (event instanceof NavigationError) {
        NProgress.done();
      }
    });

    // Handle dynamic theming
    // activePalette$.pipe(takeUntilDestroyed()).subscribe((palette) => {
    //   const body = document.querySelector("html");
    //   if (body) {
    //     body.setAttribute("data-bs-theme", palette.colorMode);
    //   }

    //   setPrimaryColor(palette.primary);

    //   // save to local storage
    //   localStorage.setItem("activePalette", palette.id);
    // });
  }

  clearEngagementStorage() {
    sessionStorage.removeItem(ENGAGEMENT_MEMBER_FIRM_ID_KEY);
  }

  protected readonly userSummary = userSummary$;
}
